import { NgOptimizedImage } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../shared/modules/lucide.module';
import {
  PaginasAtendimentoPublicoService,
  textoDaSecao,
} from '../services/paginas-atendimento-publico.service';
import { BlocoSecao, ChavePagina, CONTEUDO_PAGINAS, SecaoAtendimento } from './conteudo-paginas';

const ACENTOS = /\p{Diacritic}/gu;

const ESQUELETO = [1, 2, 3, 4];

const MENSAGEM_ERRO = 'Não foi possível carregar o conteúdo desta página.';

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}

/**
 * Perguntas frequentes, Política de atendimento e Outros canais: um componente para as três
 * rotas, escolhido pelo `data.pagina`. Cada accordion é um item publicado no adm, e o painel
 * mostra a descrição, o contato do canal e os blocos, cada bloco de canal pelo seu
 * comportamento.
 */
@Component({
  selector: 'app-pages-perguntas-politica-canais',
  templateUrl: './pages-perguntas-politica-canais.component.html',
  styleUrls: ['./pages-perguntas-politica-canais.component.css'],
  imports: [BreadcrumbComponent, LucideModule, NgOptimizedImage, RouterLink],
})
export class PagesPerguntasPoliticaCanaisComponent {
  private readonly paginasAtendimento = inject(PaginasAtendimentoPublicoService);

  readonly pagina = input<ChavePagina>('canais');

  readonly esqueleto = ESQUELETO;

  readonly conteudo = computed(
    () => CONTEUDO_PAGINAS[this.pagina()] ?? CONTEUDO_PAGINAS.canais
  );

  private readonly lista = this.paginasAtendimento.conteudo(() => this.pagina());

  readonly imagem = this.paginasAtendimento.imagem(() => this.pagina());

  readonly carregando = this.lista.carregando;

  readonly erro = computed(() => (this.lista.erro() ? MENSAGEM_ERRO : ''));

  readonly termoBusca = signal('');

  readonly abertoId = linkedSignal<unknown, string>({
    source: () => ({ pagina: this.pagina(), termo: this.termoBusca() }),
    computation: () => '',
  });

  readonly buscaBloqueada = computed(() => this.carregando() || !!this.erro());

  readonly secoesFiltradas = computed<readonly SecaoAtendimento[]>(() => {
    const termo = normalizar(this.termoBusca());
    const secoes = this.lista.secoes();
    if (!termo) return secoes;

    return secoes.filter((secao) => normalizar(textoDaSecao(secao)).includes(termo));
  });

  /** O bloco que a janela mostra: uma janela por página, com o bloco da vez. */
  readonly blocoModal = signal<BlocoSecao | null>(null);

  private readonly modal = viewChild<ElementRef<HTMLDialogElement>>('modalBloco');

  aoBuscar(evento: Event): void {
    this.termoBusca.set((evento.target as HTMLInputElement).value);
  }

  limparBusca(): void {
    this.termoBusca.set('');
  }

  tentarNovamente(): void {
    this.lista.recarregar();
  }

  alternarSecao(id: string): void {
    this.abertoId.update((atual) => (atual === id ? '' : id));
  }

  estaAberta(id: string): boolean {
    return this.abertoId() === id;
  }

  abrirModal(bloco: BlocoSecao): void {
    this.blocoModal.set(bloco);
    this.modal()?.nativeElement.showModal();
  }

  fecharModal(): void {
    this.modal()?.nativeElement.close();
  }

  /** O Esc fecha o `<dialog>` sem passar pelo botão: a limpeza fica no evento `close`. */
  aoFecharModal(): void {
    this.blocoModal.set(null);
  }
}
